import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../../services/api';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  LinearProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import EditIcon from '@mui/icons-material/Edit';
import InventoryIcon from '@mui/icons-material/Inventory';
import SearchIcon from '@mui/icons-material/Search';
import { useNavigate } from 'react-router-dom';
import {
  SMART_COLORS as COLORS,
  smartActionButtonSx,
  smartCardSx as cardSx,
  smartTableContainerSx,
  smartTableHeadSx,
  smartTableSx,
} from '../../theme/smartStableTheme';
import SettingsPageHeader from '../../components/SettingsPageHeader';
import SmartTablePagination, { useSmartPagination } from '../../components/SmartTablePagination';

const apiUrl = process.env.REACT_APP_API_URL;

const MedicationsList = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [medications, setMedications] = useState([]);
  const [selectedMedicationId, setSelectedMedicationId] = useState(null);
  // إضافة بالعلبة: حجم العلبة (وحدات) + سعرها + عدد العلب → وحدات مضافة وسعر وحدة محتسب.
  const [pkgSize, setPkgSize] = useState('');
  const [pkgPrice, setPkgPrice] = useState('');
  const [pkgCount, setPkgCount] = useState('1');
  const [disposalQty, setDisposalQty] = useState('');
  const [stockDialogOpen, setStockDialogOpen] = useState(false);
  const [disposalDialogOpen, setDisposalDialogOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const isRtl = String(i18n.language || '').startsWith('ar');

  const fetchMedications = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await api.get(`${apiUrl}/medications`);
      setMedications(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      setError(err?.response?.data?.error || t('medicationsList.loadError'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchMedications();
  }, [fetchMedications]);

  const filteredMedications = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return medications;
    return medications.filter((medication) => (
      String(medication.medication_name || '').toLowerCase().includes(term)
      || String(medication.default_unit || '').toLowerCase().includes(term)
      || String(medication.default_duration || '').toLowerCase().includes(term)
    ));
  }, [medications, search]);

  const {
    page,
    rowsPerPage,
    paginatedItems: paginatedMedications,
    handleChangePage,
    handleChangeRowsPerPage,
  } = useSmartPagination(filteredMedications, [search]);

  const openStockDialog = useCallback((medicationId) => {
    setSelectedMedicationId(medicationId);
    setPkgSize('');
    setPkgPrice('');
    setPkgCount('1');
    setStockDialogOpen(true);
  }, []);

  const openDisposalDialog = useCallback((medicationId) => {
    setSelectedMedicationId(medicationId);
    setDisposalQty('');
    setDisposalDialogOpen(true);
  }, []);

  const selectedMedication = useMemo(
    () => medications.find((m) => m.medication_id === selectedMedicationId) || null,
    [medications, selectedMedicationId]
  );
  const isFlat = selectedMedication?.pricing_mode === 'flat';

  // إضافة بالعلبة: الوحدات المضافة = حجم العلبة × عدد العلب؛ سعر الوحدة = سعر العلبة ÷ حجمها.
  // أدوية pricing_mode='flat' (3.4.87 — غير قابلة للتجزئة، مثل المغذّي): لا "حجم علبة" — عدد
  // العلب المُضافة = الكمّية مباشرة، وسعر العلبة الكاملة = سعر الوحدة (بلا قسمة).
  const pkgUnits = useMemo(() => {
    const count = Number(pkgCount || 1);
    if (isFlat) return Number.isFinite(count) && count > 0 ? count : 0;
    const size = Number(pkgSize);
    return Number.isFinite(size) && size > 0 && Number.isFinite(count) && count > 0 ? size * count : 0;
  }, [pkgSize, pkgCount, isFlat]);
  const pkgUnitPrice = useMemo(() => {
    const price = Number(pkgPrice);
    if (isFlat) return Number.isFinite(price) && price > 0 ? price : null;
    const size = Number(pkgSize);
    return Number.isFinite(size) && size > 0 && Number.isFinite(price) && price > 0
      ? +(price / size).toFixed(4)
      : null;
  }, [pkgSize, pkgPrice, isFlat]);

  const submitQuantity = useCallback(async (mode) => {
    const quantity = mode === 'stock' ? pkgUnits : Number(disposalQty);
    const unitPrice = mode === 'stock' ? pkgUnitPrice : null;
    const userId = Number(localStorage.getItem('userId'));
    if (!Number.isFinite(quantity) || quantity <= 0) {
      setError(t('medicationsList.invalidQuantity'));
      return;
    }
    if (!userId) {
      setError(t('medicationsList.userNotFound'));
      return;
    }
    setSaving(true);
    setError('');
    setMessage('');
    try {
      await api.post(`${apiUrl}/medications/${mode === 'stock' ? 'add-stock' : 'dispose-stock'}`, {
        medication_id: selectedMedicationId,
        quantity,
        ...(mode === 'stock' && unitPrice != null ? { unit_price: unitPrice } : {}),
        user_id: userId,
      });
      setStockDialogOpen(false);
      setDisposalDialogOpen(false);
      setMessage(t(mode === 'stock' ? 'medicationsList.stockAddedSuccess' : 'medicationsList.stockDisposedSuccess'));
      await fetchMedications();
    } catch (err) {
      setError(err?.response?.data?.error || t(mode === 'stock' ? 'medicationsList.stockAddError' : 'medicationsList.stockDisposeError'));
    } finally {
      setSaving(false);
    }
  }, [disposalQty, fetchMedications, selectedMedicationId, pkgUnits, pkgUnitPrice, t]);

  const handleDelete = useCallback(async () => {
    const userId = Number(localStorage.getItem('userId'));
    if (!userId) {
      setError(t('medicationsList.userNotFound'));
      return;
    }
    setSaving(true);
    setError('');
    setMessage('');
    try {
      await api.post(`${apiUrl}/medications/delete/${deleteId}`, { user_id: userId });
      setDeleteId(null);
      setMessage(t('medicationsList.deleteSuccess'));
      await fetchMedications();
    } catch (err) {
      setError(err?.response?.data?.error || t('medicationsList.deleteError'));
    } finally {
      setSaving(false);
    }
  }, [deleteId, fetchMedications, t]);

  return (
    <Box className="smart-page-container" dir={isRtl ? 'rtl' : 'ltr'} sx={{ bgcolor: COLORS.page, minHeight: '100vh', maxWidth: 'var(--app-page-max)', mx: 'auto' }}>
      <SettingsPageHeader
        title={t('medicationsList.title')}
        subtitle={t('medicationsList.pageHint')}
        count={medications.length}
        actions={(
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => navigate('/medication-add')} sx={{ bgcolor: COLORS.gold, '&:hover': { bgcolor: COLORS.goldHover }, fontWeight: 900 }}>
            {t('medicationsList.addNew')}
          </Button>
        )}
      />

      {loading && <LinearProgress sx={{ mb: 2 }} />}
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {message && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setMessage('')}>{message}</Alert>}

      <Paper elevation={0} sx={{ ...cardSx, overflow: 'hidden' }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ p: 2, borderBottom: `1px solid ${COLORS.line}` }}>
          <TextField
            size="small"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            label={t('medicationsList.search')}
            InputProps={{ startAdornment: <SearchIcon sx={{ color: COLORS.muted, mr: isRtl ? 0 : 1, ml: isRtl ? 1 : 0 }} /> }}
            sx={{ maxWidth: { sm: 420 } }}
            fullWidth
          />
        </Stack>
        <TableContainer sx={smartTableContainerSx}>
          <Table size="small" sx={smartTableSx}>
            <TableHead sx={smartTableHeadSx}>
              <TableRow>
                <TableCell align="center" sx={{ width: 72 }}>#</TableCell>
                <TableCell>{t('medicationsList.name')}</TableCell>
                <TableCell align="center">{t('medicationsList.defaultDosage')}</TableCell>
                <TableCell align="center">{t('medicationsList.defaultDuration')}</TableCell>
                <TableCell align="center">{t('medicationsList.stock')}</TableCell>
                <TableCell align="center">{t('medicationsList.defaultUnit')}</TableCell>
                <TableCell align="center" sx={{ minWidth: 210 }}>{t('medicationsList.actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedMedications.map((medication, index) => (
                <TableRow key={medication.medication_id} hover>
                  <TableCell align="center">{page * rowsPerPage + index + 1}</TableCell>
                  <TableCell sx={{ color: COLORS.text, fontWeight: 900, minWidth: 180 }}>{medication.medication_name}</TableCell>
                  <TableCell align="center">{medication.default_dosage}</TableCell>
                  <TableCell align="center">{medication.default_duration || '-'}</TableCell>
                  <TableCell align="center">{medication.stock_quantity}</TableCell>
                  <TableCell align="center">{medication.default_unit}</TableCell>
                  <TableCell align="center">
                    <Stack direction="row" spacing={1} justifyContent="center">
                      <Tooltip title={t('edit')}>
                        <IconButton onClick={() => navigate(`/medication-edit/${medication.medication_id}`)} sx={smartActionButtonSx(COLORS.blue, COLORS.blueSoft)}>
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={t('medicationsList.addtostock')}>
                        <IconButton onClick={() => openStockDialog(medication.medication_id)} sx={smartActionButtonSx(COLORS.green, COLORS.greenSoft)}>
                          <InventoryIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={t('medicationsList.dispose')}>
                        <IconButton onClick={() => openDisposalDialog(medication.medication_id)} sx={smartActionButtonSx(COLORS.warning, COLORS.warningSoft)}>
                          <DeleteSweepIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={t('delete')}>
                        <IconButton onClick={() => setDeleteId(medication.medication_id)} sx={smartActionButtonSx(COLORS.rose, COLORS.roseSoft)}>
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
              {!filteredMedications.length && !loading && (
                <TableRow>
                  <TableCell colSpan={7}>
                    <Typography sx={{ color: COLORS.muted, py: 3, textAlign: 'center' }}>
                      {search ? t('no_search_results') : t('medicationsList.noMedications')}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <SmartTablePagination count={filteredMedications.length} page={page} rowsPerPage={rowsPerPage} onPageChange={handleChangePage} onRowsPerPageChange={handleChangeRowsPerPage} />
      </Paper>

      <Dialog open={stockDialogOpen} onClose={() => setStockDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ color: COLORS.text, fontWeight: 900 }}>{t(isFlat ? 'medicationsList.addStockFlat' : 'medicationsList.addStockByPackage')}</DialogTitle>
        <DialogContent>
          {isFlat && (
            <Typography variant="caption" sx={{ color: COLORS.muted, display: 'block', mb: 1 }}>
              {t('pricingMode.hint')}
            </Typography>
          )}
          {!isFlat && (
            <TextField autoFocus type="number" label={t('medicationsList.packageSize')} fullWidth value={pkgSize} onChange={(event) => setPkgSize(event.target.value)} sx={{ mt: 1 }} inputProps={{ min: 0, step: '0.01' }} />
          )}
          <TextField type="number" label={t(isFlat ? 'medicationsList.itemPrice' : 'medicationsList.packagePrice')} fullWidth value={pkgPrice} onChange={(event) => setPkgPrice(event.target.value)} sx={{ mt: 2 }} inputProps={{ min: 0, step: '0.01' }} />
          <TextField type="number" label={t(isFlat ? 'medicationsList.itemCount' : 'medicationsList.packageCount')} fullWidth value={pkgCount} onChange={(event) => setPkgCount(event.target.value)} sx={{ mt: 2 }} inputProps={{ min: 1, step: '1' }} />
          <div style={{ background: '#f6f3ea', borderRadius: 8, padding: '8px 12px', fontSize: 14, marginTop: 16 }}>
            {t('medicationsList.unitsAdded')}: <b>{pkgUnits || 0}</b><br />
            {t(isFlat ? 'medicationsList.itemUnitPrice' : 'medicationsList.computedUnitPrice')}: <b>{pkgUnitPrice != null ? pkgUnitPrice : '—'}</b>
            {pkgUnitPrice != null && !isFlat ? ` ${t('medicationsList.unitPriceNote')}` : ''}
          </div>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setStockDialogOpen(false)} sx={{ color: COLORS.muted, fontWeight: 900 }}>{t('cancel')}</Button>
          <Button onClick={() => submitQuantity('stock')} disabled={saving} variant="contained" sx={{ bgcolor: COLORS.gold, '&:hover': { bgcolor: COLORS.goldHover }, fontWeight: 900 }}>{t('confirm')}</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={disposalDialogOpen} onClose={() => setDisposalDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ color: COLORS.text, fontWeight: 900 }}>{t('medicationsList.disposeStock')}</DialogTitle>
        <DialogContent>
          <TextField autoFocus type="number" label={t('medicationsList.enterDisposalAmount')} fullWidth value={disposalQty} onChange={(event) => setDisposalQty(event.target.value)} sx={{ mt: 1 }} inputProps={{ min: 0, step: '0.01' }} />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDisposalDialogOpen(false)} sx={{ color: COLORS.muted, fontWeight: 900 }}>{t('cancel')}</Button>
          <Button onClick={() => submitQuantity('dispose')} disabled={saving} sx={{ color: COLORS.rose, fontWeight: 900 }}>{t('confirm')}</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(deleteId)} onClose={() => setDeleteId(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ color: COLORS.text, fontWeight: 900 }}>{t('delete_confirmation')}</DialogTitle>
        <DialogContent>
          <DialogContentText>{t('medicationsList.confirmDelete')}</DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDeleteId(null)} sx={{ color: COLORS.muted, fontWeight: 900 }}>{t('cancel')}</Button>
          <Button onClick={handleDelete} disabled={saving} sx={{ color: COLORS.rose, fontWeight: 900 }}>{t('delete')}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MedicationsList;
